import React, { useEffect, useState, useMemo } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Button, Col, Row, Spinner, FormCheck, FormLabel, OverlayTrigger, Tooltip, Dropdown } from 'react-bootstrap';
import TextInput from '../../../form-generator/components/TextInput';
import {
  FileEarmarkPdfFill,
  Folder2,
  Plus,
  ExclamationCircleFill,
  ArrowRepeat,
} from 'react-bootstrap-icons';
import { toast } from 'sonner';
import SlidingSideBar from '../../SlidingSideBar/SlidingSideBar';
import Selecto from 'react-selecto';
import DriveFileUploader from '../drive-file-uploader';
import { ArrowDown, ArrowUp, ArrowUpDown, Check, FileDown, Folder, X, GripVertical, Trash2, Info } from 'lucide-react';
import Accordion from '../Accordion';

const FileRow = ({ file, onFilePickClick, pickedFiles = [], usedFiles = [] }) => {
  const isSelected = pickedFiles.find(f => f.id === file.id);
  const isUsed = usedFiles.find(f => f.id === file.id);

  return (
    <OverlayTrigger
      delay={{ show: 400, hide: 10 }}
      placement="bottom"
      overlay={<Tooltip id={`filename-tooltip-${file.id}`}>{file.fileName}</Tooltip>}
    >
      <Col xs={12} className="p-0">
        {onFilePickClick ? (
          <div
            onClick={() => onFilePickClick(file)}
            className={`position-relative text-dark selectable-item rounded-lg d-flex align-items-center justify-content-between hover-muted ${isSelected && 'bg-grey'
              }`}
            data-id={file.id}
            style={{ overflow: 'hidden' }}
            onMouseEnter={e => {
              const checkbox = e.currentTarget.querySelector('.file-checkbox');
              if (checkbox) checkbox.style.opacity = '1';
            }}
            onMouseLeave={e => {
              const checkbox = e.currentTarget.querySelector('.file-checkbox');
              if (checkbox && !isSelected) checkbox.style.opacity = '0';
            }}
          >
            {isUsed && (
              <div
                className="bg-info d-flex align-items-center justify-content-center rounded-circle"
                style={{
                  position: 'absolute',
                  top: '2px',
                  left: '6px',
                  width: '10px',
                  height: '10px',
                }}>
                <Check size={8} className="text-white" />
              </div>
            )}

            <div className="d-flex align-items-center px-2" style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <FileEarmarkPdfFill size={16} className="text-danger flex-shrink-0" />
              <div
                className={`tinyFont ml-2 ${isSelected ? 'text-info' : ''}`}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0
                }}
              >
                {file.fileName}
              </div>
            </div>

            <div
              className="file-checkbox flex-shrink-0"
              style={{
                opacity: isSelected ? '1' : '0',
                transition: 'opacity 0.2s ease'
              }}
              onClick={e => e.stopPropagation()}
            >
              <FormCheck type="checkbox" checked={!!isSelected} onChange={() => onFilePickClick(file)} />
            </div>

          </div>
        ) : (
          <a
            style={{ textDecoration: 'none' }}
            href={`https://drive.google.com/file/d/${file.id}/view`}
            target="_blank"
          >
            <div
              className="position-relative text-dark selectable-item rounded-lg d-flex align-items-center "
              data-id={file.id}
              style={{
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                overflow: 'hidden',
                padding: '2px'
              }}
            >
              <FileEarmarkPdfFill size={16} className="text-danger flex-shrink-0" />
              <div
                className={`tinyFont ml-2 ${isSelected ? 'text-info' : ''}`}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0
                }}
              >
                {file.fileName}
              </div>
            </div>
          </a>
        )}
      </Col>
    </OverlayTrigger>
  );
};

const UploadedFileCard = ({ uploadedFile, onRemove }) => {
  const { file, status, id } = uploadedFile;

  return (
    <Col className="p-0" style={{ flex: '0 0 calc(50% - 4px)', maxWidth: 'calc(50% - 4px)' }}>
      <div
        className={`position-relative p-1 text-dark rounded-lg border d-flex align-items-center bg-primary-light ${status === 'error' ? 'border-danger' : status === 'pending' ? 'border-warning' : 'border-gray'
          }`}
        style={{ overflow: 'hidden', width: '100%', maxWidth: '100%' }}
      >

        {status === 'pending' ? (
          <Spinner size="sm" variant="dark" animation="border" className="image-loading-spinner" />
        ) : status === 'error' ? (
          <ExclamationCircleFill className="text-danger ml-1" size={16} />
        ) : (
          <FileEarmarkPdfFill size={16} className="text-danger ml-1" />
        )}

        <div
          className="d-flex flex-column tinyFont ml-2"
          style={{ flex: 1, minWidth: 0, overflow: 'hidden', width: 0 }}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}
            title={file?.name}
          >
            {status === 'error' ? `Upload failed - ${file?.name}` : file?.name}
          </span>
        </div>

        {onRemove && (
          <Button
            variant="light"
            size="sm"
            className="ml-2 bg-transparent border-0 shadow-none hover-light rounded-circle p-1 d-flex align-items-center justify-content-center"
            onClick={() => onRemove(id)}
            title="Remove file"
          >
            <X size={12} />
          </Button>
        )}
      </div>
    </Col>
  );
};

const FileAndFolders = ({
  filesAndFolders,
  onFilePickClick,
  pickedFiles = [],
  handleSelectAll,
  isSelectAll = false,
  files = null,
  picker = false,
  driveFolder = null,
  addFilesToFolder,
  uploadedFilesMap: propUploadedFilesMap,
  setUploadedFilesMap: propSetUploadedFilesMap,
  usedFiles = []
}) => {
  const [localUploadedFilesMap, setLocalUploadedFilesMap] = useState({});
  const uploadedFilesMap = propUploadedFilesMap || localUploadedFilesMap;
  const setUploadedFilesMap = propSetUploadedFilesMap || setLocalUploadedFilesMap;
  const [sortBy, setSortBy] = useState({ field: 'name', direction: 'asc' });

  const folderId = filesAndFolders?.id || 'root';
  const uploadedFiles = uploadedFilesMap[folderId] || [];

  const sortOptions = [
    { label: 'Name', value: 'name', direction: 'asc' },
    { label: 'Upload Date', value: 'date', direction: 'asc' }
  ];

  // Cycle through sort states
  const SortOption = (field) => {
    if (sortBy.field !== field) {
      setSortBy({ field, direction: 'asc' });
    } else if (sortBy.direction === 'asc') {
      setSortBy({ field, direction: 'desc' });
    } else if (sortBy.direction === 'desc') {
      setSortBy({ field: null, direction: null });
    } else {
      setSortBy({ field, direction: 'asc' });
    }
  };

  const sortFiles = (files, sortConfig) => {
    if (!files || files.length === 0 || !sortConfig.field || !sortConfig.direction) return files;

    const sortDirection = sortConfig.direction === 'asc' ? 1 : -1;

    return [...files].sort((a, b) => {
      if (sortConfig.field === 'name') {
        return a.fileName.localeCompare(b.fileName) * sortDirection;
      }
      else if (sortConfig.field === 'date') {
        const dateA = a.createdTime ? new Date(a.createdTime) : new Date(0);
        const dateB = b.createdTime ? new Date(b.createdTime) : new Date(0);
        return (dateA - dateB) * sortDirection;
      }
      return 0;
    });
  };

  const sortFolders = (folders) => {
    if (!folders || folders.length === 0) return folders;

    const folderOrder = {
      'lien search': 1,
      'lien': 1,
      'estoppel': 2,
      'survey': 3
    };

    return [...folders].sort((a, b) => {
      const aName = a.folderName?.toLowerCase() || '';
      const bName = b.folderName?.toLowerCase() || '';

      const aOrder = folderOrder[aName] || 999;
      const bOrder = folderOrder[bName] || 999;

      // Sort by order first, then alphabetically as tiebreaker
      return aOrder - bOrder || aName.localeCompare(bName);
    });
  };

  const sortFolderStructure = (structure, sortConfig) => {
    if (!structure) return structure;

    return {
      ...structure,
      files: sortFiles(structure.files, sortConfig),
      folders: sortFolders(structure.folders)?.map(folder => sortFolderStructure(folder, sortConfig))
    };
  };

  // apply sorting
  const sortedFilesAndFolders = useMemo(() => {
    return sortFolderStructure(filesAndFolders, sortBy);
  }, [filesAndFolders, sortBy]);

  // Calculate if all files in a specific folder (including nested folders) are selected
  const getAllFilesInFolder = folderStructure => {
    let allFiles = [];
    if (folderStructure?.files) {
      allFiles = [...allFiles, ...folderStructure.files];
    }
    if (folderStructure?.folders) {
      folderStructure.folders.forEach(folder => {
        allFiles = [...allFiles, ...getAllFilesInFolder(folder)];
      });
    }
    return allFiles;
  };

  // Check if a specific folder has all its files selected
  const isFolderAllSelected = folder => {
    const allFilesInFolder = getAllFilesInFolder(folder);
    return (
      allFilesInFolder.length > 0 && allFilesInFolder.every(file => pickedFiles.some(picked => picked.id === file.id))
    );
  };

  const removeUploadedFile = fileId => {
    setUploadedFilesMap(prev => ({
      ...prev,
      [folderId]: prev[folderId]?.filter(f => f.id !== fileId) || []
    }));
  };

  return (
    <div>
      {sortedFilesAndFolders?.files?.length > 0 || uploadedFiles?.length > 0 ? (
        <Row className="mx-1 mb-4" style={{ gap: '2px' }}>
          <Col xs={12} className="mb-1 px-1 d-flex align-items-center justify-content-end">
            <Dropdown drop="down">
              <Dropdown.Toggle variant="outline-info" size="sm" className="d-flex align-items-center">
                {sortBy.direction === null ? <ArrowUpDown size={16} className="mr-1" /> : sortBy.direction === 'asc' ? <ArrowUp size={16} className="mr-1" /> : <ArrowDown size={16} className="mr-1" />}
                Sort By {sortBy.direction === null ? null : sortBy.field === 'name' ? ': Name' : ': Upload Date'}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ width: 'fit-content' }} popperConfig={{ strategy: 'fixed' }}>
                {sortOptions.map(option => (
                  <Dropdown.Item
                    key={option.value}
                    onClick={() => SortOption(option.value)}
                    active={sortBy.field === option.value}
                    className={`d-flex align-items-center justify-content-between smallFont px-2 ${sortBy.field === option.value ? '' : 'hover-bg-info'}`}
                    style={{
                      backgroundColor: sortBy.field === option.value ? '#0d6efd' : '#f8f9fa',
                      color: sortBy.field === option.value ? '#ffffff' : '#212529',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span>
                      {option.value === 'name' ? (
                        <>
                          Name {sortBy.field === 'name' && sortBy.direction === 'asc' && '(A-Z)'}
                          {sortBy.field === 'name' && sortBy.direction === 'desc' && '(Z-A)'}
                        </>
                      ) : (
                        <>
                          Upload Date {sortBy.field === 'date' && sortBy.direction === 'asc' && '(Oldest)'}
                          {sortBy.field === 'date' && sortBy.direction === 'desc' && '(Newest)'}
                        </>
                      )}
                    </span>
                    {sortBy.field === option.value && sortBy.direction === 'asc' && <ArrowUp size={16} className="ml-2" />}
                    {sortBy.field === option.value && sortBy.direction === 'desc' && <ArrowDown size={16} className="ml-2" />}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Col>
          <div className="border-bottom py-2 w-100">
            {picker && (
              <FormCheck
                type="checkbox"
                label="Select All"
                onChange={() => handleSelectAll({ folderName: null })}
                checked={isSelectAll}
                className="mb-2 largeFont"
              />
            )}
          </div>
          {sortedFilesAndFolders?.files?.map(f => (
            <FileRow key={f._id || f.id} file={f} onFilePickClick={onFilePickClick} pickedFiles={pickedFiles} usedFiles={usedFiles} />
          ))}
          {uploadedFiles?.map(uploadedFile => (
            <UploadedFileCard key={uploadedFile.id} uploadedFile={uploadedFile} onRemove={removeUploadedFile} />
          ))}

          {(sortedFilesAndFolders?.files?.length > 0 || uploadedFiles?.length > 0) && (
            <div className="border-bottom py-2 w-100" />
          )}
        </Row>
      ) : sortedFilesAndFolders?.folderName ? (
        <div className="d-flex flex-row align-items-center justify-content-center p-2 text-center">
          <Folder2 size={20} className="text-muted mb-1" />
          <h5 className="smallFont text-muted mb-1 ml-2">No files found...</h5>
        </div>
      ) : null}
      {sortedFilesAndFolders?.folders?.length > 0 && (
        <div>
          {sortedFilesAndFolders?.folders?.map(f => (
            <Accordion
              key={f.id}
              title={
                <>
                  {f?.folderName && (
                    <div className="d-flex align-items-center">
                      {picker && (
                        <div onClick={e => e.stopPropagation()}>
                          <FormCheck
                            type="checkbox"
                            onChange={() => handleSelectAll({ folderName: f?.folderName })}
                            checked={isFolderAllSelected(f)}
                            className=" largeFont"
                          />
                        </div>
                      )}
                      <div className="d-flex align-items-center ml-1 ">
                        <Folder size={34} className="mr-2 text-info rounded-lg p-2 bg-info-light" />
                        <div className="text-left">
                          <h6 className=" font-weight-bold mb-1 xsmallFont">{f?.folderName}</h6>
                          <p className="xtinyFont font-weight-light mb-0">
                            {f?.files?.length} files |{' '}
                            <span className="text-info font-weight-bold">
                              {
                                getAllFilesInFolder(f).filter(file => pickedFiles.some(picked => picked.id === file.id))
                                  .length
                              }{' '}
                              Selected
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              }
              additionalComponent={
                <>
                  {f?.id && driveFolder && (
                    <div className=" rounded-lg p-1">
                      <DriveFileUploader
                        showTitle={false}
                        uploadButtonText="Upload"
                        multiple={true}
                        onFileSave={uploadedFile => {
                          const newFile = {
                            fileName: uploadedFile.file.name,
                            id: uploadedFile.fileId,
                            mimeType: uploadedFile.file.type
                          };

                          addFilesToFolder([newFile], f.id);
                          setUploadedFilesMap(prev => ({
                            ...prev,
                            [f.id]: []
                          }));
                          toast.success('File uploaded successfully to drive');
                        }}
                        folderId={f.id}
                        showUploadedFiles={false}
                        filesToBeUploaded={uploadedFiles}
                        onUploadedFilesChange={files => {
                          setUploadedFilesMap(prev => ({
                            ...prev,
                            [f.id]: files
                          }));
                        }}
                      />
                    </div>
                  )}
                </>
              }
              className="border-bottom "
              triggerClassName="hover-muted py-3"
              defaultOpen={true}
            >
              <div className="ml-2">
                <FileAndFolders
                  filesAndFolders={f}
                  onFilePickClick={onFilePickClick}
                  pickedFiles={pickedFiles}
                  handleSelectAll={handleSelectAll}
                  isSelectAll={isSelectAll}
                  files={files}
                  picker={picker}
                  driveFolder={driveFolder}
                  addFilesToFolder={addFilesToFolder}
                  uploadedFilesMap={uploadedFilesMap}
                  setUploadedFilesMap={setUploadedFilesMap}
                  usedFiles={usedFiles}
                />
              </div>
            </Accordion>
          ))}
        </div>
      )}
    </div>
  );
};

export const DriveFilePicker = ({
  driveFolder,
  onFilePickClick,
  defaultPickedFiles = [],
  label,
  driveFiles,
  driveFilesLoading,
  onUpdateDriveFiles,
  handleRefresh,
  usedFiles = [],
}) => {
  const [showDriveReader, setShowDriveReader] = useState(false);
  const [pickedFile, setPickedFile] = useState([...defaultPickedFiles]);


  const handleFileDeleteClick = file => {
    const updatedFiles = pickedFile.filter(f => f.id !== file.id);
    setPickedFile(updatedFiles);
    onFilePickClick(updatedFiles);
  };

  // Handle drag end event to reorder picked files
  const handleOnDragEnd = result => {
    const { destination, source } = result;

    if (!destination) return;

    const items = Array.from(pickedFile);
    const [removed] = items.splice(source.index, 1);
    items.splice(destination.index, 0, removed);

    setPickedFile(items);
    onFilePickClick(items);
  };

  useEffect(() => {
    setPickedFile(defaultPickedFiles);
  }, [defaultPickedFiles]);

  return (
    <div className="card border rounded-lg overflow-hidden" style={{ height: '200px' }}>
      <div className="d-flex align-items-center justify-content-between border-bottom px-3 py-2">
        <FormLabel className=" font-weight-semibold d-flex align-items-center mb-0">
          {label} <span className="text-muted tinyFont ml-1">({pickedFile.length})</span>
        </FormLabel>
        <Button
          variant="light"
          size="sm"
          onClick={() => setShowDriveReader(true)}
          className="d-flex bg-white hover-light align-items-center border"
        >
          <FileDown size={14} /> <span className="ml-1">Select from Drive</span>
        </Button>
      </div>
      {pickedFile.length > 0 ? (
        <Row style={{ height: 'calc(100% - 50px)', overflow: 'hidden' }}>
          <Col
            className="py-2 px-4"
            style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}
          >
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable
                droppableId="droppable-pick-files"
                direction="vertical"
              >
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: '100%',
                    }}
                  >
                    {pickedFile.map((f, index) => {
                      const isAddressMatched = f?.isAddressMatched ?? undefined;

                      return (
                        <div key={f.id}>
                          <Draggable
                            key={f.id}
                            draggableId={f.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="rounded-lg hover-muted"
                                style={{
                                  ...provided.draggableProps.style,
                                  backgroundColor: snapshot.isDragging ? '#f8f9fa' : '',
                                  boxShadow: snapshot.isDragging
                                    ? '0 4px 6px rgba(0,0,0,0.1)'
                                    : 'none',
                                  cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                }}
                                onMouseEnter={e => {
                                  const gripIcon = e.currentTarget.querySelector('.grip-icon');
                                  if (gripIcon) gripIcon.style.opacity = '1';
                                }}
                                onMouseLeave={e => {
                                  const gripIcon = e.currentTarget.querySelector('.grip-icon');
                                  if (gripIcon) gripIcon.style.opacity = '0';
                                }}
                              >
                                <div className="h-100 d-flex align-items-center justify-content-between p-1">
                                  <div className="d-flex align-items-center overflow-hidden">
                                    <GripVertical size={16} className="text-muted grip-icon flex-shrink-0" style={{ opacity: 0, transition: 'opacity 0.2s' }} />

                                    {/* Delete button */}
                                    <Trash2
                                      size={16}
                                      className="cursor-pointer text-danger flex-shrink-0 mx-1 mr-3"
                                      onClick={() => handleFileDeleteClick(f)}
                                    />

                                    <FileEarmarkPdfFill size={16} className="text-danger flex-shrink-0" />
                                    <div className="ml-2 d-flex align-items-center" style={{ minWidth: 0, gap: '4px' }}>
                                      <span
                                        className="tinyFont"
                                        style={{
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          minWidth: 0,
                                        }}
                                      >
                                        {f.fileName}
                                      </span>
                                      {isAddressMatched === true && (
                                        <span className="tinyFont text-success d-flex align-items-center">
                                          <Check size={12} className="mr-1" />
                                        </span>
                                      )}
                                      {isAddressMatched === false && (
                                        <span className="tinyFont text-danger">
                                          <Info size={12} className="mr-1" />
                                          Address on the invoice doesn’t match the order’s property address.
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        </div>
                      )
                    })}

                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

          </Col>
        </Row>
      ) : (
        <button
          className="d-flex flex-column align-items-center justify-content-center p-4 text-center border-0 bg-transparent mx-auto"
          onClick={() => setShowDriveReader(true)}
        >
          <FileEarmarkPdfFill size={32} className="text-muted mb-1" />
          <h5 className="largeFont text-muted mb-2">No files selected</h5>
        </button>
      )}
      <DriveFileReader
        show={showDriveReader}
        onHide={() => setShowDriveReader(false)}
        driveFolder={driveFolder}
        onFilePickClick={files => {
          setPickedFile(files);
          onFilePickClick(files);
        }}
        pickedFiles={pickedFile}
        picker={true}
        driveFiles={driveFiles}
        driveFilesLoading={driveFilesLoading}
        onUpdateDriveFiles={onUpdateDriveFiles}
        handleRefresh={handleRefresh}
        usedFiles={usedFiles}
      />
    </div>
  );
};

const DriveFileReader = ({
  show,
  onHide,
  sidebarZIndex,
  onFileDeleteClick,
  driveFolder,
  onFilePickClick,
  pickedFiles = [],
  picker = false,
  driveFiles,
  driveFilesLoading,
  onUpdateDriveFiles,
  handleRefresh,
  usedFiles = []
}) => {
  const [files, setFiles] = useState();
  const [fileLoading, setFileLoading] = useState();
  const [selectedFiles, setSelectedFiles] = useState([...pickedFiles]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const handleFileSelect = file => {
    setSelectedFiles(prev => {
      const exists = prev.find(f => f.id === file.id);
      return exists ? prev.filter(f => f.id !== file.id) : [...prev, file];
    });
  };

  const handleSave = e => {
    e.stopPropagation();
    onFilePickClick(selectedFiles);
    onHide();
  };

  // function to get files from a folder structure with optional filtering
  const getFilesFromStructure = (folderStructure, options = {}) => {
    const { targetFolderName = null, includeAllFiles = false } = options;
    let files = [];

    if (targetFolderName) {
      // Check if current folder matches the target folder name
      if (folderStructure?.folderName === targetFolderName && folderStructure?.files) {
        files = [...files, ...folderStructure.files];
      }
    } else if (includeAllFiles) {
      if (folderStructure?.files) {
        files = [...files, ...folderStructure.files];
      }
    }

    // Recursively search in subfolders
    if (folderStructure?.folders) {
      folderStructure.folders.forEach(folder => {
        const subFolderFiles = getFilesFromStructure(folder, options);
        files = [...files, ...subFolderFiles];
      });
    }

    return files;
  };

  // Helper functions for backward compatibility
  const getAllFiles = folderStructure => {
    return getFilesFromStructure(folderStructure, { includeAllFiles: true });
  };

  const getFilesByFolderName = (folderStructure, targetFolderName) => {
    return getFilesFromStructure(folderStructure, { targetFolderName });
  };

  // search drive files and folders
  const searchFilesAndFolders = (folderStructure, query) => {
    if (!query || !folderStructure) return folderStructure;

    const lowerQuery = query.toLowerCase();

    // Filter files that match the query
    const filteredFiles =
      folderStructure.files?.filter(file => file.fileName?.toLowerCase().includes(lowerQuery)) || [];

    // Recursively filter folders
    const filteredFolders =
      folderStructure.folders
        ?.map(folder => {
          const filteredFolder = searchFilesAndFolders(folder, query);

          // Include folder if it has matching files, or if its name matches, or if it has matching subfolders
          const hasMatchingName = folder.folderName?.toLowerCase().includes(lowerQuery);
          const hasMatchingFiles = filteredFolder.files?.length > 0;
          const hasMatchingSubfolders = filteredFolder.folders?.length > 0;

          if (hasMatchingName || hasMatchingFiles || hasMatchingSubfolders) {
            return filteredFolder;
          }

          return null;
        })
        .filter(Boolean) || [];

    return {
      ...folderStructure,
      files: filteredFiles,
      folders: filteredFolders
    };
  };

  // Filter files based on search query
  const filteredFiles = useMemo(() => {
    return searchFilesAndFolders(files, searchQuery);
  }, [files, searchQuery]);

  const handleSelectAll = ({ folderName = null }) => {
    if (!filteredFiles) return;

    if (folderName) {
      const filesToSelect = getFilesByFolderName(filteredFiles, folderName);
      const isFolderCurrentlySelected = selectedFiles.some(f => filesToSelect.some(fts => fts.id === f.id));

      if (isFolderCurrentlySelected) {
        // Remove all files from this folder
        setSelectedFiles(prev => prev.filter(f => !filesToSelect.some(fts => fts.id === f.id)));
      } else {
        // Add all files from this folder (avoiding duplicates)
        setSelectedFiles(prev => {
          const existingIds = prev.map(f => f.id);
          const newFiles = filesToSelect.filter(f => !existingIds.includes(f.id));
          return [...prev, ...newFiles];
        });
      }
    } else {
      const allFiles = getAllFiles(filteredFiles);

      if (isSelectAll) {
        setSelectedFiles([]);
        setIsSelectAll(false);
      } else {
        setSelectedFiles(allFiles);
        setIsSelectAll(true);
      }
    }
  };

  const handleClickAndDrag = e => {
    const ids = e.selected.map(el => el.getAttribute('data-id'));

    setSelectedFiles(prev => {
      const allFiles = filteredFiles ? getAllFiles(filteredFiles) : [];
      const newSelected = allFiles.filter(f => ids.includes(f.id));

      // Merge with existing selection if shift is held
      if (e.inputEvent.shiftKey) {
        const merged = [...prev, ...newSelected].filter((f, i, arr) => arr.findIndex(x => x.id === f.id) === i);
        return merged;
      }

      // Replace selection otherwise
      return newSelected;
    });
  };

  const addFilesToFolder = (updatedFiles, folderId) => {
    setFiles(prev => {
      if (!prev) return prev;

      // Helper function to recursively find and update folder
      const updateFolderRecursively = folder => {
        if (folder.id === folderId) {
          // Found the target folder, add files to the beginning (unshift)
          return {
            ...folder,
            files: [...updatedFiles, ...(folder.files || [])]
          };
        }

        // If this folder has subfolders, search recursively
        if (folder.folders && folder.folders.length > 0) {
          return {
            ...folder,
            folders: folder.folders.map(updateFolderRecursively)
          };
        }

        return folder;
      };

      // Update the files state
      const updatedFilesState = {
        ...prev,
        folders: prev.folders.map(updateFolderRecursively)
      };

      // Notify parent component to update cached data
      if (onUpdateDriveFiles) {
        onUpdateDriveFiles(updatedFilesState);
      }

      return updatedFilesState;
    });
  };

  useEffect(() => {
    if (!show || !driveFolder) {
      setSearchQuery('');
      setSelectedFiles([...pickedFiles]);
      setFiles(driveFiles || []);
      setFileLoading(false);
      return;
    }

    setFiles(driveFiles);
    setFileLoading(driveFilesLoading);
  }, [show, driveFolder, driveFiles, driveFilesLoading]);

  useEffect(() => {
    if (filteredFiles) {
      const allFiles = getAllFiles(filteredFiles);
      const allSelected =
        allFiles.length > 0 && allFiles.every(file => selectedFiles.some(selected => selected.id === file.id));
      setIsSelectAll(allSelected);
    }
  }, [selectedFiles, filteredFiles]);

  return (
    <>
      <SlidingSideBar
        width={'50%'}
        zIndex={sidebarZIndex}
        visible={show}
        onClose={onHide}
        title={picker ? 'Select Files' : 'View Files'}
        additionalComponent={
          <Button size="sm" variant="outline-dark" onClick={handleRefresh} className="d-flex align-items-center h-100">
            <ArrowRepeat size={20} className="mr-1" />
            Refresh
          </Button>
        }
        dock="left"
      >
        <div style={{ height: '100%' }}>
          <TextInput
            variant="text"
            hint="Search files..."
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
            }}
            className="my-2"
          />
          <div style={{ paddingBottom: '80px' }}>
            {driveFilesLoading || !filteredFiles || !filteredFiles?.folders?.length ? (
              driveFilesLoading ? (
                <div className="p-5 text-center">
                  <Spinner size="sm" variant="dark" animation="border" />
                </div>
              ) : (
                <div
                  className="d-flex flex-column align-items-center justify-content-center p-5 text-center"
                  style={{ minHeight: '300px' }}
                >
                  <Folder2 size={80} className="text-muted mb-4" />
                  <h5 className="largeFont text-muted mb-2">No files or folders found</h5>
                  <p className="text-muted mb-0">Add files or folders to the Drive to see them here.</p>
                </div>
              )
            ) : (
              <FileAndFolders
                filesAndFolders={filteredFiles}
                onFilePickClick={picker ? handleFileSelect : null}
                pickedFiles={selectedFiles}
                handleSelectAll={handleSelectAll}
                isSelectAll={isSelectAll}
                picker={picker}
                driveFolder={driveFolder}
                addFilesToFolder={addFilesToFolder}
                usedFiles={usedFiles}
              />
            )}

            {/* --- Drag Selection --- */}
            <Selecto
              container={document.body}
              selectableTargets={['.selectable-item']}
              hitRate={10}
              selectByClick={false} // don't override your normal click
              selectFromInside={true}
              toggleContinueSelect={['shift']} // shift+click = toggle specific file
              onSelect={handleClickAndDrag}
            />
          </div>

          {!driveFilesLoading && selectedFiles?.length && picker ? (
            <div
              className="bg-white w-100 p-2"
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                boxShadow: '0 -2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <div className="d-flex justify-content-end">
                <Button variant="success" onClick={handleSave} className="d-flex align-items-center gap-2 shadow">
                  <Plus size={20} className="mr-2" />
                  <span>Save ({selectedFiles.length})</span>
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </SlidingSideBar>
    </>
  );
};

export default DriveFileReader;
